import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { AppKeyInterface } from '@vendasta/automata';
import { combineLatest, Observable } from 'rxjs';
import { AppPartnerService } from '@galaxy/marketplace-apps';
import {
  AUTOMATION_MARKET_ID_INJECTION_TOKEN$,
  AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
  AUTOMATION_PARTNER_ID_INJECTION_TOKEN$,
} from '@galaxy/automata/shared';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { App, AppKey } from '@vendasta/marketplace-apps';
import { AccountsService, ActivationStatus } from '@vendasta/accounts';

@Component({
  selector: 'automata-app-requirements-alert',
  templateUrl: './app-requirements-alert.component.html',
  styleUrls: ['./app-requirements-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, GalaxyAlertModule],
})
export class AppRequirementsAlertComponent implements OnInit {
  @Input() requiredApps: AppKeyInterface[] = [];

  message$: Observable<string>;
  showProductRequirementBanner$: Observable<boolean>;

  constructor(
    private readonly appPartnerService: AppPartnerService,
    private readonly accountsService: AccountsService,
    private readonly translateService: TranslateService,
    @Inject(AUTOMATION_PARTNER_ID_INJECTION_TOKEN$) private readonly partnerId$: Observable<string>,
    @Inject(AUTOMATION_MARKET_ID_INJECTION_TOKEN$) private readonly marketId$: Observable<string>,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
  ) {}

  ngOnInit() {
    this.message$ = combineLatest([this.partnerId$, this.marketId$, this.namespace$]).pipe(
      switchMap(([partnerId, marketId, namespace]) => {
        return this.appPartnerService.getMulti(this.requiredApps as AppKey[], partnerId, marketId, {}, false).pipe(
          switchMap((apps) => {
            if (!apps || apps.length === 0) {
              throw new Error('No required apps found');
            }

            return this.accountsService.getMultiActivationStatuses([namespace]).pipe(
              map((activationStatuses) => {
                const inactiveApps = apps.filter((app) => {
                  const appStatus = activationStatuses.find(
                    (status) => status.productId === app.key.appId && status.editionId === app.key.editionId,
                  );
                  return !appStatus || appStatus.status !== ActivationStatus.ACTIVATION_STATUS_ACTIVATED;
                });

                if (inactiveApps.length === 0) {
                  return { message: null };
                }

                let message;
                if (inactiveApps.length === 1) {
                  message = this.translateService.instant('AUTOMATIONS.APP_REQUIREMENTS_ALERT.SINGLE_APP', {
                    appName: this.appName(inactiveApps[0]),
                  });
                } else {
                  let names = '';
                  for (let i = 0; i < inactiveApps.length - 1; i++) {
                    names += this.appName(inactiveApps[i]);
                    if (inactiveApps.length > 2) {
                      if (i !== inactiveApps.length - 2) {
                        names += ', ';
                      } else {
                        names += ',';
                      }
                    }
                  }
                  const lastAppName = this.appName(inactiveApps[inactiveApps.length - 1]);
                  message = this.translateService.instant('AUTOMATIONS.APP_REQUIREMENTS_ALERT.MULTIPLE_APPS', {
                    appNames: names,
                    lastAppName,
                  });
                }

                return { message };
              }),
            );
          }),
        );
      }),
      map((result) => result.message),
      catchError(() => {
        return this.translateService.stream('AUTOMATIONS.APP_REQUIREMENTS_ALERT.BACKUP_MESSAGE');
      }),
      take(1),
    );

    this.showProductRequirementBanner$ = this.message$.pipe(map((message) => !!message));
  }

  private appName(app: App): string {
    if (app.sharedMarketingInformation.editionName) {
      return `${app.sharedMarketingInformation.name} | ${app.sharedMarketingInformation.editionName}`;
    }
    return `${app.sharedMarketingInformation.name}`;
  }
}
